import React, { useState, useEffect, useRef } from "react";
import { InputProps } from "@copilotkit/react-ui";
import { useCopilotChat } from "@copilotkit/react-core";
import { Box, SvgIcon } from "@mui/material";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { StriimInputField } from "@striim/striim-ui-v2";
import { StriimButton } from "@striim/striim-ui";
import RightArrowIcon from "../../../../../src/generic/icon/customIcons/Arrow_Forward.svg";
import EditIcon from "src/generic/icon/customIcons/edit.svg";
import App from "app";
import AiGradientButton from "../ai-gradient-button/ai-gradient-button";
import { useAppSelector } from "src/stores/redux/hooks";
import { selectIsHealthy } from "src/stores/redux/features/striim-ai/striim-ai.slice";
import { AI_AGENT_NAME } from "../../ai-constants";
// @ts-ignore
import { useAgent, UseAgentUpdate } from "@copilotkit/react-core/v2";
import TQLExpandModal from "./tql-expand-modal";
import { TQL_PATTERN } from "./extract-tql";
import { isAwaitingToolResponse } from "./pending-tool-call";

const styles = {
    tqlToolbar: {
        display: "flex",
        justifyContent: "flex-end",
        mb: 0.5
    },
    editAsTqlIcon: {
        width: 14,
        height: 14,
        fill: "currentColor"
    }
};

const isTQL = (text: string) => TQL_PATTERN.test(text);

const CustomInput: React.FC<InputProps> = ({ inProgress, onSend, isVisible }) => {
    const isHealthy = useAppSelector(selectIsHealthy);
    const isUnhealthy = !isHealthy;
    const { stopGeneration } = useCopilotChat();

    const { agent } = useAgent({
        agentId: "striim_ai",
        updates: [UseAgentUpdate.OnMessagesChanged, UseAgentUpdate.OnRunStatusChanged]
    });

    const isHILPresent = isAwaitingToolResponse(agent?.messages ?? []);
    const isInputDisabled = isHILPresent || inProgress || isUnhealthy;
    const [value, setValue] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const prevInProgressRef = useRef<boolean>(inProgress);
    const isInputDisabledRef = useRef<boolean>(isInputDisabled);
    const onSendRef = useRef(onSend);

    isInputDisabledRef.current = isInputDisabled;
    onSendRef.current = onSend;

    const handleSubmit = () => {
        if (isInputDisabled) return;
        if (value.trim()) onSend(value);
        setValue("");
    };

    useEffect(() => {
        const handleUserMessage = (text: string) => {
            onSendRef.current(text.trim());
        };
        const handleRun = (text: string) => {
            if (!isInputDisabledRef.current && text?.trim()) {
                onSendRef.current(text.trim());
            }
        };
        App.vent.on("striimAI:user_message", handleUserMessage);
        App.vent.on("striimAI:run", handleRun);
        return () => {
            App.vent.off("striimAI:user_message", handleUserMessage);
            App.vent.off("striimAI:run", handleRun);
        };
    }, []);

    // Focus input when it becomes enabled (disabled changes from true to false)
    useEffect(() => {
        if (prevInProgressRef.current === true && inProgress === false) {
            const activeEl = document.activeElement;
            if (!activeEl) {
                inputRef.current?.focus();
                return;
            }
            const canReceiveInput = activeEl.matches(
                'input, textarea, select, button, a[href], [contenteditable], [tabindex]:not([tabindex="-1"])'
            );
            if (!canReceiveInput) {
                inputRef.current?.focus();
            }
        }
        prevInProgressRef.current = inProgress;
    }, [inProgress]);
    return (
        <Box m={2} sx={{ position: "relative" }}>
            {/* {inProgress && (
                <LinearProgress
                    color="secondary"
                    sx={{
                        width: "98%",
                        top: 0,
                        left: "4px",
                        right: 0,
                        height: 4,
                        borderRadius: 2
                    }}
                />
            )} */}
            {isTQL(value) && (
                <Box sx={styles.tqlToolbar}>
                    <StriimButton
                        startIcon={<SvgIcon component={EditIcon as React.ElementType} sx={styles.editAsTqlIcon} />}
                        onClick={() => setModalOpen(true)}
                    >
                        Edit as TQL
                    </StriimButton>
                </Box>
            )}
            <StriimInputField
                inputRef={inputRef}
                InputProps={{
                    onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.code === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }
                }}
                disabled={isInputDisabled}
                multiline
                rows={4}
                value={value}
                onChange={value => setValue(value)}
                placeholder={isUnhealthy ? `${AI_AGENT_NAME} is unreachable` : `Ask ${AI_AGENT_NAME}`}
                id="message-input"
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px"
                }}
                data-test-id="submit-message-button"
            >
                {inProgress ? (
                    <AiGradientButton icon={<StopRoundedIcon />} onClick={stopGeneration} size={50} />
                ) : (
                    <AiGradientButton
                        icon={<RightArrowIcon />}
                        onClick={handleSubmit}
                        size={50}
                        disabled={isInputDisabled || !value}
                    />
                )}
            </Box>
            <TQLExpandModal open={modalOpen} value={value} onChange={setValue} onClose={() => setModalOpen(false)} />
        </Box>
    );
};

export default CustomInput;
